const DRAFT_KEY = "billingRateDraft_v1";
export const DRAFT_SAVED_EVENT = "billing-rate-draft-saved";
export const DRAFT_PRUNED_EVENT = "billing-rate-draft-pruned";
export const MAX_DRAFT_COUNT = 5;

const readStore = () => {
    try {
        const raw = localStorage.getItem(DRAFT_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const writeStore = (store) => {
    try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(store));
        return true;
    } catch (err) {
        console.warn("[billing-rate-draft] Failed to write draft to localStorage:", err);
        return false;
    }
};

const draftTimestamp = (entry) =>
    entry?.meta?.savedAt ?? entry?.meta?.createdAt ?? "1970-01-01T00:00:00.000Z";

const dispatchPruned = (removed) => {
    if (!removed.length || typeof window === "undefined") return;
    console.info(
        `[billing-rate-draft] Removed ${removed.length} old draft(s) (keeping ${MAX_DRAFT_COUNT} most recent)`,
    );
    window.dispatchEvent(new CustomEvent(DRAFT_PRUNED_EVENT, { detail: { removedGroupIds: removed } }));
};

/** Keeps the newest drafts by savedAt; optionally never evicts protected groupIds. */
export function pruneDrafts(store, { maxCount = MAX_DRAFT_COUNT, protectGroupIds = [] } = {}) {
    const entries = Object.entries(store ?? {});
    if (entries.length <= maxCount) return { store: { ...store }, removed: [] };

    const protectedSet = new Set(protectGroupIds.filter(Boolean));
    const sorted = [...entries].sort(([idA, a], [idB, b]) => {
        const byTime = new Date(draftTimestamp(b)) - new Date(draftTimestamp(a));
        if (byTime !== 0) return byTime;
        return idA.localeCompare(idB);
    });

    const kept = [];
    const removed = [];
    for (const [id, entry] of sorted) {
        if (protectedSet.has(id) || kept.length < maxCount) kept.push([id, entry]);
        else removed.push(id);
    }
    return { store: Object.fromEntries(kept), removed };
};

const applyPrune = (store, options) => {
    const { store: pruned, removed } = pruneDrafts(store, options);
    if (removed.length === 0) return { ok: true, removed };
    const ok = writeStore(pruned);
    if (ok) dispatchPruned(removed);
    return { ok, removed };
};

// meta: { groupId, planType, clientName }
// data: full payload object
// groupId is the stable, unique key — one slot per submission group
export const saveDraft = (meta, data) => {
    const id = meta.groupId;
    if (!id) return false;
    const store = readStore();
    const existing = store[id];
    store[id] = {
        meta: {
            ...meta,
            createdAt: existing?.meta?.createdAt ?? new Date().toISOString(),
            savedAt: new Date().toISOString(),
        },
        data,
    };
    const { store: pruned, removed } = pruneDrafts(store, {
        maxCount: MAX_DRAFT_COUNT,
        protectGroupIds: [id],
    });
    const ok = writeStore(pruned);
    if (ok && removed.length > 0) dispatchPruned(removed);
    if (ok && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(DRAFT_SAVED_EVENT, { detail: { groupId: id } }));
    }
    return ok;
};

/** Prune stale drafts once on app load (previous sessions). Idempotent when already at cap. */
export const pruneDraftsOnLoad = () => {
    const store = readStore();
    return applyPrune(store, { maxCount: MAX_DRAFT_COUNT });
};

export const loadDraft = (groupId) => {
    const store = readStore();
    return store[groupId] ?? null;
};

// Returns { [groupId]: meta } — cheap listing without deserializing payloads
export const loadAllDraftsMeta = () => {
    const store = readStore();
    return Object.fromEntries(
        Object.entries(store).map(([id, entry]) => [id, entry.meta]),
    );
};

export const clearDraft = (groupId) => {
    if (!groupId) return;
    const store = readStore();
    delete store[groupId];
    writeStore(store);
};

export const clearDrafts = (groupIds) => {
    const ids = [...new Set((groupIds ?? []).filter(Boolean))];
    if (ids.length === 0) return;
    const store = readStore();
    let changed = false;
    for (const id of ids) {
        if (store[id]) {
            delete store[id];
            changed = true;
        }
    }
    if (changed) writeStore(store);
};
